import autobind from 'autobind-decorator';
import { Url } from '../constants/environment';
import Cookie from './cookie';

@autobind
class WebSocketUtil {
  constructor(topic) {
    // topic 인자 => array 타입
    this.topic = topic || [];
    this.url = Url.webSocket;
    this.ws = null;

    // 웹푸시 디버깅을 위한 윈도우 객체 생성
    window.push_debug = false;

    this.is_ie = false;
    this.subscribe_callback_list = [];

    // socket connection 상태 값(true : 연결, false : 비연결)
    this.socket_open = false;

    // Check IE
    var trident = navigator.userAgent.match(/Trident\/(\d)/i);
    if (trident != null) {
      this.is_ie = true;
    } else {
      this.is_ie = false;
    }

    this.init();
  }

  /**
   * Initialize
   *
   * @param host 서버 호스트
   * @param port 서버 포트
   * @param callback 서버 연결시 Callback
   */
  init() {
    // 지원여부 체크
    if ('WebSocket' in window) {
      try {
        const reconnect = function () {
          setTimeout(
            function () {
              this.init();
            }.bind(this),
            2000
          );
        }.bind(this);

        this.ws = new WebSocket(this.url);

        this.ws.onopen = (event) => {
          this.socket_open = true;

          if (this.topic.length > 0) {
            this.topic.forEach((data) => {
              this.sub(data);
            });
          }

          if (this.is_ie === true) {
            if (this.ping_handler) {
              clearInterval(this.ping_handler);
            }
            this.ping_handler = setInterval(
              function () {
                this.ping();
              }.bind(this),
              58000
            );
          }
        };

        this.ws.onmessage = (event) => {
          if (event && event.data) {
            try {
              const data = event.data;
              
              if (data === 'OK' || data === 'ERROR' || data === 'PONG') {
                return;
              }
              
              let messageData;
              try {
                messageData = JSON.parse(data);
              } catch (parseError) {
                return;
              }
              
              if (messageData && messageData.TOPIC) {
                const topic = messageData.TOPIC;
                let message = messageData.MESSAGE;
                
                // Parse MESSAGE if it's a string (URL-encoded JSON from ToastMessage)
                let parsedMessage = message;
                if (typeof message === 'string') {
                  try {
                    // First, try to parse as JSON (may be URL-encoded)
                    let decoded = message;
                    try {
                      decoded = decodeURIComponent(message);
                    } catch (e) {
                      // Not URL-encoded, use as is
                    }
                    
                    const parsed = JSON.parse(decoded);
                    
                    // Check if there's a nested Message field (with capital M)
                    if (parsed && parsed.Message) {
                      if (typeof parsed.Message === 'string') {
                        // Parse the nested Message JSON string
                        try {
                          parsedMessage = JSON.parse(parsed.Message);
                        } catch (e) {
                          // If parse fails, use the outer parsed object
                          parsedMessage = parsed;
                        }
                      } else {
                        parsedMessage = parsed.Message;
                      }
                    } else {
                      parsedMessage = parsed;
                    }
                  } catch (e) {
                    // If first parse fails, try direct parse
                    try {
                      parsedMessage = JSON.parse(message);
                    } catch (e2) {
                      // Keep as string
                    }
                  }
                }
                
                // Early check for force logout - intercept before calling callback
                // This ensures force logout is handled even if handler is not registered
                // Check both the parsed message and the original message structure
                let isForceLogout = false;
                
                if (parsedMessage && typeof parsedMessage === 'object') {
                  isForceLogout = 
                    parsedMessage.category === 'forceLogout' || 
                    parsedMessage.notificationType === 'forceLogout' ||
                    (parsedMessage.AlertID && parsedMessage.AlertID.includes('force_logout'));
                }
                
                // Also check the outer structure (AlertID in the outer Message object)
                if (!isForceLogout && typeof message === 'string') {
                  try {
                    const outerParsed = JSON.parse(message);
                    if (outerParsed && outerParsed.AlertID && outerParsed.AlertID.includes('force_logout')) {
                      isForceLogout = true;
                      // Extract the inner Message if it exists
                      if (outerParsed.Message && typeof outerParsed.Message === 'string') {
                        try {
                          parsedMessage = JSON.parse(outerParsed.Message);
                        } catch (e) {
                          // Use outer parsed
                          parsedMessage = outerParsed;
                        }
                      }
                    }
                  } catch (e) {
                    // Ignore
                  }
                }
                
                if (isForceLogout) {
                    // Set flag FIRST to prevent any reloads
                    window.forceLogoutInProgress = true;
                    
                    // Stop logout detector if exists
                    if (window.logoutDetectorCleanup && typeof window.logoutDetectorCleanup.stop === 'function') {
                      window.logoutDetectorCleanup.stop();
                    }
                    
                    // Stop getInfo polling if exists
                    if (window.getInfoPollingInterval) {
                      clearInterval(window.getInfoPollingInterval);
                      delete window.getInfoPollingInterval;
                    }
                    
                    // Clear cookies immediately using Cookie.deleteCookie (with proper domain)
                    try {
                      Cookie.deleteCookie('mb_id');
                      Cookie.deleteCookie('session_id');
                      Cookie.deleteCookie('auth_token');
                      // PHPSESSID might have different domain, try both ways
                      Cookie.deleteCookie('PHPSESSID');
                      // Also try direct method for PHPSESSID with domain
                      const hostname = window.location.hostname;
                      let domain = '';
                      if (hostname.startsWith('dev-m.')) {
                        domain = '.' + hostname.replace('dev-m.', '');
                      } else {
                        domain = hostname.includes('.') ? '.' + hostname.split('.').slice(-2).join('.') : hostname;
                      }
                      document.cookie = `PHPSESSID=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${domain};`;
                    } catch (e) {
                      // Silent fail
                    }
                    
                    // Use notificationTitle and notificationText from server
                    const alertTitle = parsedMessage.notificationTitle || '로그아웃';
                    const alertMessage = parsedMessage.notificationText || '관리자에 의해 로그아웃되었습니다.';
                    
                    // Save to sessionStorage and redirect immediately
                    try {
                      sessionStorage.setItem('forceLogout', JSON.stringify({
                        title: alertTitle,
                        message: alertMessage,
                        type: parsedMessage.type || 'block'
                      }));
                    } catch (e) {}
                    
                    // Clear localStorage and redirect
                    try {
                      localStorage.removeItem('access_token');
                      localStorage.removeItem('refresh_token');
                    } catch (e) {}
                    
                    window.location.replace('/auth/login');
                    
                    // DON'T call callback - we've already handled everything
                    // Calling callback might cause duplicate redirect or reload
                    return;
                  }
                
                // Call callback only if NOT force logout
                // Pass the parsed message to callback
                if (this.subscribe_callback_list[topic]) {
                  this.subscribe_callback_list[topic](parsedMessage);
                }
              }
            } catch (error) {
              // Silent fail
            }
          }
        };

        this.ws.onerror = function (error) {
          this.log('socket error : ' + error);
          if (this.socket_open === false) {
            reconnect();
          }
        }.bind(this);
      } catch (err) {
        // Silent fail
      }
    }
  }

  /**
   * Sub
   * @param topic 구독명
   * @param callback 구독중인 메세지 전달시 Callback
   */
  sub(topic, callback) {
    if (callback) {
      this.subscribe_callback_list[topic] = callback;
    }
    if (this.socket_open) {
      this.ws.send(`SUB ${topic}`);
    } else {
      setTimeout(() => {
        this.sub(topic, callback);
      }, 1000);
    }
  }

  /**
   * SubPrivate - Subscribe private topic với session verification
   * @param topic Topic (Eg: named.notification.dev.private_{mb_id}) - mb_id đã có trong topic
   * @param sessionId Session ID / Token
   * @param callback Callback when a subscribed message is delivered
   */
  subPrivate(topic, sessionId, callback) {
    this.subscribe_callback_list[topic] = callback;
    
    if (this.socket_open && this.ws) {
      const subMessage = 'SUB ' + topic + ' ' + sessionId;
      this.ws.send(subMessage);
    } else {
      const checkAndSubscribe = () => {
        if (this.socket_open && this.ws) {
          const subMessage = 'SUB ' + topic + ' ' + sessionId;
          this.ws.send(subMessage);
        } else {
          setTimeout(checkAndSubscribe, 500);
        }
      };
      setTimeout(checkAndSubscribe, 500);
    }
  }

  /**
   * Unsub
   *
   * @param topic 구독명
   */
  unsub(topic) {
    if (this.socket_open) {
      this.ws.send('UNSUB ' + topic);
    }
  }

  /**
   * Unsuball
   */
  unsuball() {
    if (this.socket_open) {
      this.subscribe_callback_list = [];
      this.ws.send('UNSUBALL');
    }
  }

  /**
   * Ping
   */
  ping() {
    if (this.socket_open) {
      this.ws.send('PING');
    }
  }

  /**
   * Debug
   *
   * @param is_debug 디버그 모드 설정 여부
   */
  debug(is_debug) {
    this.debug_mode = is_debug;
  }

  /**
   * Log
   *
   * @param msg 로그 메세지
   */
  log(msg) {
    if (window.push_debug) {
      if (
        typeof window != 'undefined' &&
        window != null &&
        window.console != null
      ) {
        console.log(msg);
      }
    }
  }

  /**
   * socket Close
   */
  async asyncClose() {
    this.ws.close();
    this.ws.onmessage = null;
    this.ws = null;
    // this.subscribe_callback_list = [];
    return await true;
  }

  close(callback) {
    this.ws.close();
    this.ws.onclose = () => {
      this.log('socket closed');
      this.socket_open = false;
      this.ws = null;
      callback();
    };
    this.ws.onmessage = null;
    // this.subscribe_callback_list = [];
  }
}

export default WebSocketUtil;
